import { RotateCw, Repeat2, RefreshCw, CalendarDays, Clock, ChevronRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Transaction, RecurrencePattern } from "@/types/transaction";
import { getRecurrenceSequence, shouldDisplaySequence } from "@/lib/utils/recurrenceSequence";

interface RecurringIndicatorProps {
  transaction: Transaction;
  variant?: "icon" | "badge" | "full" | "sequence" | "unified" | "progress" | "minimal";
  className?: string;
  allTransactions?: Transaction[]; // Opcional: lista completa para cálculo de sequência
}

const getRecurrencePatternText = (pattern: RecurrencePattern, interval: number = 1): string => {
  const patternMap = {
    daily: interval === 1 ? "Diário" : `A cada ${interval} dias`,
    weekly: interval === 1 ? "Semanal" : `A cada ${interval} semanas`,
    monthly: interval === 1 ? "Mensal" : `A cada ${interval} meses`,
    yearly: interval === 1 ? "Anual" : `A cada ${interval} anos`,
  };

  return patternMap[pattern] || pattern;
};

const getRecurrenceTooltipText = (transaction: Transaction): string => {
  const {
    recurrence_pattern,
    recurrence_interval,
    recurrence_count,
    recurrence_created_count,
    recurrence_end_date,
  } = transaction;

  if (!recurrence_pattern) return "Transação recorrente";

  const patternText = getRecurrencePatternText(recurrence_pattern, recurrence_interval);

  let tooltipText = patternText;

  if (recurrence_count && recurrence_created_count !== undefined) {
    const remaining = recurrence_count - recurrence_created_count;
    if (remaining > 0) {
      tooltipText += ` • ${remaining} ocorrências restantes`;
    }
  }

  if (recurrence_end_date) {
    const endDate = new Date(recurrence_end_date).toLocaleDateString('pt-BR');
    tooltipText += ` • Termina em ${endDate}`;
  }

  return tooltipText;
};

export const RecurringIndicator = ({
  transaction,
  variant = "icon",
  className,
  allTransactions
}: RecurringIndicatorProps) => {
  // Safety check: if transaction is undefined or null, don't render
  if (!transaction) {
    return null;
  }

  // For sequence variant, allow child transactions
  if (variant !== "sequence" && transaction.parent_transaction_id) {
    return null;
  }

  // Only show for recurring transactions
  if (!transaction.is_recurring) {
    return null;
  }

  const tooltipText = getRecurrenceTooltipText(transaction);

  if (variant === "badge") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className={cn(
              "text-xs font-medium bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100",
              className
            )}
          >
            <RotateCw className="w-3 h-3 mr-1" />
            Recorrente
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (variant === "full") {
    return (
      <div className={cn("flex items-center gap-2 text-xs text-muted-foreground", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <RotateCw className="w-3 h-3 text-purple-600" />
              <span className="text-purple-700 font-medium">
                {getRecurrencePatternText(
                  transaction.recurrence_pattern!,
                  transaction.recurrence_interval
                )}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  if (variant === "sequence") {
    const sequence = getRecurrenceSequence(transaction, allTransactions);

    if (!sequence) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-purple-500/10 text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 transition-colors duration-200 cursor-help",
              className
            )}
            aria-label={`Ocorrência ${sequence.current} de ${sequence.total || 'ilimitada'}`}
          >
            {/* Integrated Icon */}
            <div className="flex items-center justify-center">
              <Repeat2 className="w-3 h-3 text-purple-600 dark:text-purple-400 transition-colors" />
            </div>

            {/* Unified Sequence Display */}
            <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
              {sequence.current}/{sequence.total || '∞'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{tooltipText}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Ocorrência {sequence.current} de {sequence.total || 'ilimitada'}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // New unified variant - modern circular design
  if (variant === "unified") {
    const sequence = getRecurrenceSequence(transaction, allTransactions);

    if (!sequence) {
      return null;
    }

    const progressPercentage = sequence.total ? (sequence.current / sequence.total) * 100 : 0;
    const circumference = 2 * Math.PI * 8; // radius = 8
    const strokeDashoffset = circumference - (progressPercentage / 100) * circumference;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="relative group cursor-help">
            {/* Progress Circle Background */}
            <svg className="w-6 h-6 transform -rotate-90">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                className="text-purple-100"
              />
              {/* Progress Circle */}
              {sequence.total && (
                <circle
                  cx="12"
                  cy="12"
                  r="8"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  className="text-purple-600 transition-all duration-500"
                  strokeLinecap="round"
                />
              )}
            </svg>

            {/* Center Content - Icon + Number */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex items-center">
                {sequence.current === 1 ? (
                  <Repeat2 className="w-2.5 h-2.5 text-purple-700" />
                ) : (
                  <span className="text-[8px] font-bold text-purple-700 leading-none">
                    {sequence.current}
                  </span>
                )}
              </div>
            </div>

            {/* Sequence Text */}
            <span className="ml-2 text-xs text-muted-foreground group-hover:text-purple-600 transition-colors">
              {sequence.total ? `de ${sequence.total}` : 'recorrente'}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{tooltipText}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Progresso: {Math.round(progressPercentage)}% completo
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Progress variant - horizontal progress bar with sequence
  if (variant === "progress") {
    const sequence = getRecurrenceSequence(transaction, allTransactions);

    if (!sequence) {
      return null;
    }

    const progressPercentage = sequence.total ? (sequence.current / sequence.total) * 100 : 0;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 group cursor-help">
            {/* Icon */}
            <RefreshCw className="w-3 h-3 text-purple-600 group-hover:rotate-180 transition-transform duration-500" />

            {/* Progress Bar */}
            <div className="w-12 h-1.5 bg-border rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            {/* Sequence Text */}
            <span className="text-xs font-medium text-muted-foreground group-hover:text-purple-600 transition-colors">
              {sequence.current}
              {sequence.total && `/${sequence.total}`}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{tooltipText}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Ocorrência {sequence.current} de {sequence.total || 'ilimitada'}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Minimal variant - clean icon + text
  if (variant === "minimal") {
    const sequence = getRecurrenceSequence(transaction, allTransactions);

    if (!sequence) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex items-center gap-1 text-xs text-muted-foreground hover:text-purple-600 transition-colors cursor-help",
              className
            )}
            aria-label={`Ocorrência ${sequence.current} de ${sequence.total || 'ilimitada'}`}
          >
            <RefreshCw className="w-3 h-3" />
            <span className="font-medium">
              {sequence.current}{sequence.total ? `/${sequence.total}` : ''}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{tooltipText}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Ocorrência {sequence.current} de {sequence.total || 'ilimitada'}
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Default icon variant
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex items-center justify-center w-5 h-5 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors cursor-help",
            className
          )}
        >
          <RotateCw className="w-3 h-3" />
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p className="text-sm">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
};

// Helper function to check if a transaction is recurring
export const isRecurringTransaction = (transaction: Transaction): boolean => {
  return !!(transaction.is_recurring && !transaction.parent_transaction_id);
};

// Helper function to check if a transaction is a child of a recurring transaction
export const isChildTransaction = (transaction: Transaction): boolean => {
  return !!transaction.parent_transaction_id;
};

// Helper function to get recurrence status
export const getRecurrenceStatus = (transaction: Transaction): {
  isActive: boolean;
  remainingCount?: number;
  endDate?: Date;
  progress?: number;
} => {
  if (!transaction.is_recurring || transaction.parent_transaction_id) {
    return { isActive: false };
  }

  let remainingCount: number | undefined;
  let progress: number | undefined;

  if (transaction.recurrence_count && transaction.recurrence_created_count !== undefined) {
    remainingCount = transaction.recurrence_count - transaction.recurrence_created_count;
    progress = (transaction.recurrence_created_count / transaction.recurrence_count) * 100;
  }

  const endDate = transaction.recurrence_end_date ? new Date(transaction.recurrence_end_date) : undefined;
  const isActive = (remainingCount === undefined || remainingCount > 0) &&
                   (!endDate || endDate >= new Date());

  return {
    isActive,
    remainingCount,
    endDate,
    progress
  };
};